/**
 * @author Nihar <nihar.baishya@314ecorp.com>
 * @description Batch Era Posting Button
 */
import { useMemo, useState } from 'react';
import _ from 'lodash';
import { commentLabel } from './ReconcilePayment';
import { Button, ButtonProps, Descriptions, Flex, Input, Modal, Typography } from 'antd';
import useBatchPostPayment from 'hooks/payments/mutate/useBatchPostPayment';
import useAllClaimStatusRequests from 'hooks/claim/useAllClaimStatusRequests';
import { formatAmount } from 'utils/accounts/allAccounts';
import { prepareManualWorklistModificationCriteria } from 'utils/claims/workLists';
import { AntClass } from '@veritable/common/constants/antd';
import { EMPTY_VALUE } from '@veritable/common/constants';

const POST_CRITERIA_MESSAGE = '(Only claims in the "Adjudicated" state will be posted).';

export interface IProps extends ButtonProps {
	tableSelectionMeta: ITableSelectionMeta;
}

/**
 * Batch Era Posting button
 * @param {IProps} props props
 * @returns {React.FC} Batch Era Posting
 */
const BatchEraPosting: React.FC<IProps> = ({ tableSelectionMeta, ...restProps }) => {
	const [note, setNote] = useState('');
	const [isModalOpen, setIsModalOpen] = useState(false);
	const { mutate, isPending } = useBatchPostPayment();
	const criteria = useMemo(() => prepareManualWorklistModificationCriteria(tableSelectionMeta), [tableSelectionMeta]);
	const {
		data: firstAcceptedClaimData,
		isLoading: isLoadingFirstAcceptedClaim,
		isError: isErrorFirstAcceptedClaim,
	} = useAllClaimStatusRequests({ apiParams: { criteria }, pageNumber: 1, pageSize: 1 }, { enabled: isModalOpen });
	const acceptedClaimsCount = firstAcceptedClaimData?.count || 0;
	const {
		data: allAcceptedClaimsData,
		isLoading: isLoadingAllAcceptedClaims,
		isError: isErrorAllAcceptedClaims,
	} = useAllClaimStatusRequests(
		{ apiParams: { criteria }, pageNumber: 1, pageSize: acceptedClaimsCount },
		{ enabled: isModalOpen && acceptedClaimsCount > 0 },
	);

	const loading = isLoadingFirstAcceptedClaim || isLoadingAllAcceptedClaims;
	const error = isErrorFirstAcceptedClaim || isErrorAllAcceptedClaims;

	const totalPaidAmount = formatAmount(_.sumBy(_.map(allAcceptedClaimsData?.data, (item) => item?.claimMetaData?.claimAmount || 0)));

	// function to handle cancel button click
	const handleCancel = () => {
		setIsModalOpen(false);
		setNote('');
	};

	// function to handle ok button click
	const handleOk = () => {
		mutate(
			{ requestParams: { batchClaimPostPaymentRequest: { claims: { criteria }, notes: note || null } } },
			{ onSuccess: handleCancel },
		);
	};

	// function to get claim count label
	const claimCountLabel = useMemo(() => {
		return `${acceptedClaimsCount} ${acceptedClaimsCount === 1 ? 'claim' : 'claims'}`;
	}, [acceptedClaimsCount]);

	const getContent = () => {
		if (acceptedClaimsCount > 0) {
			return (
				<Flex vertical gap={16}>
					<Flex vertical gap={8}>
						{'Please verify the details before posting payment.'}
						<Typography.Text italic type='secondary'>
							{POST_CRITERIA_MESSAGE}
						</Typography.Text>
					</Flex>
					<Descriptions layout='vertical' column={2} colon={false} className='allocation-overview-descriptions'>
						<Descriptions.Item label={'Claims Count'}>{acceptedClaimsCount || EMPTY_VALUE}</Descriptions.Item>
						<Descriptions.Item label={'To be Posted'}>{totalPaidAmount || EMPTY_VALUE}</Descriptions.Item>
					</Descriptions>
					<Flex vertical gap={8}>
						{commentLabel}
						<Input.TextArea
							placeholder={'Enter your comment here (optional)'}
							value={note}
							onChange={(e) => setNote(e.target.value)}
							data-testid='ipr-batch-post-note'
						/>
					</Flex>
				</Flex>
			);
		}
		if (error) {
			return (
				<Flex vertical gap={16}>
					<Typography.Text strong className='text-base text-red-500'>
						{'Error in validating claims for batch post payment.'}
					</Typography.Text>
				</Flex>
			);
		}
		return (
			<Flex vertical gap={8}>
				<Typography.Text strong className='text-base'>
					{'None of the selected claims are eligible for batch posting.'}
				</Typography.Text>
				<Typography.Text italic type='secondary'>
					{POST_CRITERIA_MESSAGE}
				</Typography.Text>
			</Flex>
		);
	};

	return (
		<>
			<Button
				type='primary'
				className={AntClass.secondaryButton}
				onClick={() => setIsModalOpen(true)}
				disabled={tableSelectionMeta?.selectCount === 0}
				data-testid='ipr-batch-post-btn'
				{...restProps}
			>
				{`Post Payment ${tableSelectionMeta?.selectCount === 0 ? '' : `(${tableSelectionMeta?.selectCount})`}`}
			</Button>
			<Modal
				title={'Confirmation'}
				destroyOnHidden
				open={isModalOpen}
				onCancel={handleCancel}
				okText={`Confirm ${claimCountLabel}`}
				onOk={handleOk}
				loading={loading}
				data-testid='ipr-batch-post-modal'
				okButtonProps={{ loading: isPending, 'data-testid': 'ipr-batch-post-confirm' }}
				cancelButtonProps={{ disabled: isPending, 'data-testid': 'ipr-batch-post-cancel' }}
				{...(acceptedClaimsCount === 0
					? {
							footer: (
								<Button type='primary' onClick={handleCancel} data-testid='ipr-batch-post-close'>
									{'Close'}
								</Button>
							),
						}
					: {})}
			>
				{getContent()}
			</Modal>
		</>
	);
};

export default BatchEraPosting;
